import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from './index';
import {
  User,
  WorkSchedule,
  Meeting,
  NotificationLog,
  AvailableSlotInfo,
  DashboardAnalytics,
  MeetingStatus,
} from '../types';

const API_BASE_URL = 'http://52.21.219.92:3000';

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Auth', 'User', 'Schedule', 'Meeting', 'Notification'],
  endpoints: (builder) => ({
    // Auth Endpoints
    login: builder.mutation({
      query: (credentials) => ({
        url: '/auth/admins/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth'],
    }),
    forgotPassword: builder.mutation({
      query: (data) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body: data,
      }),
    }),

    // User Management
    getUsers: builder.query<User[], void>({
      query: () => '/users',
      providesTags: ['User'],
    }),
    createUser: builder.mutation<User, Partial<User>>({
      query: (body) => ({
        url: '/users',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    updateUser: builder.mutation<User, { id: string; data: Partial<User> }>({
      query: ({ id, data }) => ({
        url: `/users/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),

    // Work Schedules
    getSchedules: builder.query<WorkSchedule[], void>({
      query: () => '/reception/schedules',
      providesTags: ['Schedule'],
    }),
    getAvailableSlots: builder.query<{ schedule: WorkSchedule | null; slots: AvailableSlotInfo[] }, string | void>({
      query: (date) => (date ? `/reception/schedules/available?date=${date}` : '/reception/schedules/available'),
      providesTags: ['Schedule', 'Meeting'],
    }),
    createSchedule: builder.mutation<WorkSchedule, Partial<WorkSchedule>>({
      query: (body) => ({
        url: '/reception/schedules',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Schedule'],
    }),

    // Meetings
    getMeetings: builder.query<Meeting[], { status?: MeetingStatus; date?: string } | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.status) queryParams.append('status', params.status);
        if (params?.date) queryParams.append('date', params.date);
        return `/reception/meetings?${queryParams.toString()}`;
      },
      providesTags: ['Meeting'],
    }),
    getPendingMeetings: builder.query<Meeting[], void>({
      query: () => '/reception/meetings/pending',
      providesTags: ['Meeting'],
    }),
    getTodayMeetings: builder.query<Meeting[], void>({
      query: () => '/reception/meetings/today',
      providesTags: ['Meeting'],
    }),
    getMeetingDetails: builder.query<Meeting, string>({
      query: (id) => `/reception/meetings/${id}`,
      providesTags: ['Meeting'],
    }),
    getDashboardAnalytics: builder.query<DashboardAnalytics, void>({
      query: () => '/reception/meetings/analytics',
      providesTags: ['Meeting', 'Schedule'],
    }),
    createMeeting: builder.mutation<Meeting, Partial<Meeting>>({
      query: (body) => ({
        url: '/reception/meetings',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Meeting', 'Schedule'],
    }),
    approveMeeting: builder.mutation<Meeting, { id: string; adminRemarks?: string }>({
      query: ({ id, adminRemarks }) => ({
        url: `/reception/meetings/${id}/approve`,
        method: 'PATCH',
        body: { adminRemarks },
      }),
      invalidatesTags: ['Meeting', 'Notification'],
    }),
    rejectMeeting: builder.mutation<Meeting, { id: string; adminRemarks?: string }>({
      query: ({ id, adminRemarks }) => ({
        url: `/reception/meetings/${id}/reject`,
        method: 'PATCH',
        body: { adminRemarks },
      }),
      invalidatesTags: ['Meeting'],
    }),

    // Notifications
    getNotificationLogs: builder.query<NotificationLog[], void>({
      query: () => '/reception/notifications/logs',
      providesTags: ['Notification'],
    }),
    retryNotification: builder.mutation<NotificationLog, string>({
      query: (logId) => ({
        url: `/reception/notifications/retry/${logId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Notification'],
    }),
  }),
});

export const {
  useLoginMutation,
  useForgotPasswordMutation,
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetSchedulesQuery,
  useGetAvailableSlotsQuery,
  useCreateScheduleMutation,
  useGetMeetingsQuery,
  useGetPendingMeetingsQuery,
  useGetTodayMeetingsQuery,
  useGetMeetingDetailsQuery,
  useGetDashboardAnalyticsQuery,
  useCreateMeetingMutation,
  useApproveMeetingMutation,
  useRejectMeetingMutation,
  useGetNotificationLogsQuery,
  useRetryNotificationMutation,
} = api;
